// #355 — why the session ended, carried across the redirect to the login page.
//
// A failed token renewal clears localStorage and navigates to '/', so any
// explanation has to survive a full page load. sessionStorage does: it is
// per-tab (the tab that was thrown out is the one that should say so) and
// localStorage.clear() does not touch it.
//
// Before this, that path was completely silent. On a deploy that invalidates
// every token at once — which is what #345 does — everyone working in the app
// simply lands on the login screen mid-task, with unsaved work gone and nothing
// to say whether they mistyped something, were thrown out, or the app broke.

export const SESSION_END_KEY = 'sessionEnd'

// The record has three independent pieces, because the message needs all three:
//   reason: 'throttled' — the renewal itself was refused with a 429, so a login
//                         right now may be refused too. The per-IP auth budget
//                         (#327) was spent by the app's own renewals, not by
//                         anybody mistyping a password, so the copy must not
//                         blame the person.
//           'expired'   — the refresh token is simply no longer accepted (30-day
//                         expiry, a suspended user, a rotated app.rsa, #345).
//   updated: the API answers with a different X-App-Version than this bundle was
//            built against, i.e. it was deployed while the tab was open. That is
//            the #345 mass-invalidation case, and worth saying out loud: it turns
//            "I was thrown out for no reason" into "the app was updated".
//   retryAfter: seconds to wait, when the server advertised one.
export function sessionEndFromRefreshError(error) {
  const response = error?.response
  return {
    reason: response?.status === 429 ? 'throttled' : 'expired',
    updated: serverWasUpdated(response),
    retryAfter: parseRetryAfter(response?.headers?.['retry-after']),
  }
}

function serverWasUpdated(response) {
  const serverVersion = response?.headers?.['x-app-version']
  if (serverVersion && serverVersion !== __APP_VERSION__) {
    return true
  }
  // A successful response earlier in this page's life may already have spotted
  // the mismatch and raised the update banner (request.js onResponseSuccess).
  return window.vue?.$store?.state?.versionMismatch === true
}

// parseRetryAfter reads the seconds out of a Retry-After header. Only readable
// cross-origin because the API now lists it in Access-Control-Expose-Headers
// (#355) — before that the browser dropped it and the wait could not be shown.
// The HTTP-date form of Retry-After is not produced by this API, so only the
// delta-seconds form is handled; anything else yields null and the message
// falls back to wording that states no time at all.
export function parseRetryAfter(value) {
  const seconds = parseInt(value, 10)
  if (Number.isNaN(seconds) || seconds <= 0) {
    return null
  }
  return seconds
}

export function recordSessionEnd({ reason, updated, retryAfter }) {
  try {
    sessionStorage.setItem(SESSION_END_KEY, JSON.stringify({ reason, updated, retryAfter }))
  } catch {
    // A full or blocked sessionStorage must never stop the logout redirect.
  }
}

// consumeSessionEnd reads the record and clears it: the notice belongs to the
// login screen the redirect lands on, not to every later visit to that screen.
export function consumeSessionEnd() {
  let raw = null
  try {
    raw = sessionStorage.getItem(SESSION_END_KEY)
    sessionStorage.removeItem(SESSION_END_KEY)
  } catch {
    return null
  }
  if (!raw) {
    return null
  }
  try {
    const parsed = JSON.parse(raw)
    return parsed && parsed.reason ? parsed : null
  } catch {
    return null
  }
}
